import StatusIndicator from '@risk-smart/themed-cloudscape-components/status-indicator';
import { ChevronRight } from '@untitled-ui/icons-react';
import clsx from 'clsx';
import type { FC } from 'react';

import styles from './ToolCallRenderer.module.scss';
import { toolLabel } from './toolLabels';
import type { AgentMetadata } from './useChatStore';

type ToolCall = NonNullable<AgentMetadata['toolCalls']>[number];

const statusFor = (
  toolCall: ToolCall
): { type: 'loading' | 'success' | 'error'; label: string } => {
  const label = toolLabel(toolCall.toolName);
  if (!toolCall.endTime) {
    return { type: 'loading', label: `Running - ${label}` };
  }

  return toolCall.success === false
    ? { type: 'error', label: `${label} — failed` }
    : { type: 'success', label };
};

/** Truncated, readable peek at a tool's input/result for the expandable body. */
const peek = (value: unknown, max = 600): string | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const text =
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);

  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const sectionLabelClass = 'text-xs font-semibold text-grey600';
const preClass =
  'mt-1 max-h-[240px] overflow-auto whitespace-pre-wrap rounded border border-grey300 bg-white p-2 text-xs';
const cardClass =
  'overflow-hidden rounded-md border border-grey300 bg-white text-sm';
const summaryClass =
  'flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-grey150';
// Expanded body: a tinted panel divided from the summary, so opened content
// reads as a distinct box (à la Claude's tool-call disclosure). The theme grey
// scale starts at grey150 (#EDEDF2), which read too heavy — use a lighter
// arbitrary tint with a visible divider.
const bodyClass =
  'flex flex-col gap-2 border-t border-grey300 bg-[#FAFAFC] px-3 py-3';

/** A single tool call: name + status header, expandable to input/result/error. */
const ToolCallCard: FC<{ toolCall: ToolCall }> = ({ toolCall }) => {
  const status = statusFor(toolCall);
  const args = peek(toolCall.toolArgs);
  const result = peek(toolCall.result);
  const hasBody = args !== null || result !== null || !!toolCall.error;

  return (
    <details
      className={clsx(styles.toolCall, cardClass)}
      data-testid={'tool-call'}
    >
      <summary className={summaryClass} title={toolCall.toolName}>
        <ChevronRight
          width={16}
          height={16}
          aria-hidden={true}
          className={clsx(
            styles.chevron,
            'shrink-0',
            hasBody ? 'text-grey500' : 'invisible'
          )}
        />
        <StatusIndicator type={status.type}>{status.label}</StatusIndicator>
        {toolCall.endTime && toolCall.executionTimeMs !== undefined && (
          <span className={'ml-auto shrink-0 text-xs text-grey500'}>
            {`${toolCall.executionTimeMs}ms`}
          </span>
        )}
      </summary>
      {hasBody && (
        <div className={bodyClass}>
          {toolCall.error && (
            <div>
              <strong className={sectionLabelClass}>{'Error'}</strong>
              <pre className={preClass}>{toolCall.error}</pre>
            </div>
          )}
          {args !== null && (
            <div>
              <strong className={sectionLabelClass}>{'Input'}</strong>
              <pre className={preClass}>{args}</pre>
            </div>
          )}
          {result !== null && (
            <div>
              <strong className={sectionLabelClass}>{'Result'}</strong>
              <pre className={preClass}>{result}</pre>
            </div>
          )}
        </div>
      )}
    </details>
  );
};

const keyFor = (toolCall: ToolCall, index: number): string =>
  toolCall.toolUseId ?? `${toolCall.toolName}-${toolCall.startTime}-${index}`;

/**
 * Renders the read-only Agent's tool calls beneath a message. To avoid crowding
 * the chat, only the currently-running call(s) are shown inline; completed calls
 * are tucked behind a single openable container (and once the turn finishes,
 * every call collapses into that container). Each card shows the tool name +
 * status and expands to the input / result / error. Fed from the chat store's
 * `toolCalls` metadata — used only for agent-mode messages.
 */
export const ToolCallRenderer: FC<{ toolCalls: ToolCall[] }> = ({
  toolCalls,
}) => {
  if (toolCalls.length === 0) {
    return null;
  }

  const running = toolCalls.filter((toolCall) => !toolCall.endTime);
  const isActive = running.length > 0;
  // While a call is running, show the running one(s) and collapse the finished
  // ones; once nothing is running, collapse everything into the summary.
  const current = isActive ? running : [];
  const collapsed = isActive
    ? toolCalls.filter((toolCall) => toolCall.endTime)
    : toolCalls;

  const collapsedLabel = isActive
    ? `${collapsed.length} completed step${collapsed.length === 1 ? '' : 's'}`
    : `Used ${toolCalls.length} tool${toolCalls.length === 1 ? '' : 's'}`;

  return (
    <div className={'mt-2 flex flex-col gap-2'} data-testid={'tool-calls'}>
      {collapsed.length > 0 && (
        <details
          className={clsx(styles.toolCall, cardClass)}
          data-testid={'tool-calls-archive'}
        >
          <summary className={clsx(summaryClass, 'text-grey600')}>
            <ChevronRight
              width={16}
              height={16}
              aria-hidden={true}
              className={clsx(styles.chevron, 'shrink-0 text-grey500')}
            />
            <span className={'text-xs font-semibold'}>{collapsedLabel}</span>
          </summary>
          <div className={bodyClass}>
            {collapsed.map((toolCall, index) => (
              <ToolCallCard key={keyFor(toolCall, index)} toolCall={toolCall} />
            ))}
          </div>
        </details>
      )}
      {current.map((toolCall, index) => (
        <ToolCallCard key={keyFor(toolCall, index)} toolCall={toolCall} />
      ))}
    </div>
  );
};
